/**
 * Formats a duration in milliseconds to a human-readable string using Intl.DurationFormat.
 *
 * Uses "narrow" style for compact display (e.g., "2d 4h" instead of "2 days 4 hours").
 */
export const formatDuration = (milliseconds: number): string => {
  // Clamp to 0 to avoid negative durations
  const ms = Math.max(0, milliseconds);

  const totalMinutes = Math.floor(ms / (1000 * 60));
  const totalHours = Math.floor(ms / (1000 * 60 * 60));
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(days / 7);

  // Less than a minute
  if (totalMinutes < 1) {
    return '<1m';
  }

  // Build duration object based on magnitude
  let duration: Intl.DurationInput;

  if (days >= 30) {
    // Over 30 days: use weeks and remaining days
    const remainingDays = days % 7;
    duration = remainingDays > 0 ? { days: remainingDays, weeks } : { weeks };
  } else if (days >= 7) {
    // Over 7 days: just days (drop hours for readability)
    duration = { days };
  } else if (days >= 1) {
    // 1-6 days: days and hours
    const remainingHours = totalHours % 24;
    duration = remainingHours > 0 ? { days, hours: remainingHours } : { days };
  } else if (totalHours >= 1) {
    // Under 1 day: hours and minutes
    const remainingMinutes = totalMinutes % 60;
    duration = remainingMinutes > 0 ? { hours: totalHours, minutes: remainingMinutes } : { hours: totalHours };
  } else {
    // Under 1 hour: just minutes
    duration = { minutes: totalMinutes };
  }

  const formatter = new Intl.DurationFormat('en', { style: 'narrow' });
  return formatter.format(duration);
};

/**
 * Calculates the duration between two dates and formats it.
 */
export const formatDurationBetween = (startDate: Date | string, endDate: Date | string): string => {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;
  const milliseconds = end.getTime() - start.getTime();
  return formatDuration(milliseconds);
};

/**
 * Calculates the elapsed time from a start date to now and formats it.
 */
export const formatElapsedTime = (startDate: Date | string): string => {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const milliseconds = Date.now() - start.getTime();
  return formatDuration(milliseconds);
};
